import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ingredientsApi } from '@/api/ingredients';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/providers/ToastProvider';
import { getErrorMessage } from '@/api/client';
import {
  Button, Input, Badge, Spinner, EmptyState, ConfirmDialog,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell, Card,
} from '@/components/ui';
import { Plus, Pencil, Trash2, Search, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { useCurrencyFormatter } from '../../providers/AppSettingsProvider';

export default function IngredientsListPage() {
  const { t }           = useTranslation();
  const { isAdmin }     = useAuth();
  const { toast }       = useToast();
  const qc              = useQueryClient();
  const formatMoney     = useCurrencyFormatter();
  const navigate        = useNavigate();

  const [search,    setSearch]    = useState('');
  const [page,      setPage]      = useState(1);
  const [deleteId,  setDeleteId]  = useState<string | null>(null);
  const [sortCol,   setSortCol]   = useState<string>('name');
  const [sortDir,   setSortDir]   = useState<'asc' | 'desc'>('asc');
  const limit = 50;

  function toggleSort(col: string) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  function SortIcon({ col }: { col: string }) {
    if (sortCol !== col) return <ChevronsUpDown size={13} className="ml-1 inline opacity-40" />;
    return sortDir === 'asc'
      ? <ChevronUp size={13} className="ml-1 inline" />
      : <ChevronDown size={13} className="ml-1 inline" />;
  }

  function SortHead({ col, right, children }: { col: string; right?: boolean; children: React.ReactNode }) {
    return (
      <TableHead
        className={`cursor-pointer select-none hover:text-foreground${right ? ' text-right' : ''}`}
        onClick={() => toggleSort(col)}
      >
        {children}<SortIcon col={col} />
      </TableHead>
    );
  }

  const { data, isLoading } = useQuery({
    queryKey: ['ingredients', { page, search }],
    queryFn:  () => ingredientsApi.list({ page, limit, search: search || undefined }),
  });

  const deleteMutation = useMutation({
    mutationFn: ingredientsApi.delete,
    onSuccess: () => {
      toast(t('ingredients.deleted'));
      qc.invalidateQueries({ queryKey: ['ingredients'] });
      setDeleteId(null);
    },
    onError: (err) => toast(getErrorMessage(err), 'error'),
  });

  function formatCost(v: number | null | undefined) {
    return v != null ? formatMoney(Number(v)) : '—';
  }
  const f2 = (v: number | null | undefined) => v != null ? Number(v).toFixed(2) : '—';

  const sortedData = [...(data?.data ?? [])].sort((a, b) => {
    const av = (a as unknown as Record<string, unknown>)[sortCol];
    const bv = (b as unknown as Record<string, unknown>)[sortCol];
    const aStr = av == null ? '' : String(av).toLowerCase();
    const bStr = bv == null ? '' : String(bv).toLowerCase();
    const aNum = Number(av);
    const bNum = Number(bv);
    const numeric = !isNaN(aNum) && !isNaN(bNum) && av !== '' && bv !== '';
    const cmp = numeric ? aNum - bNum : aStr.localeCompare(bStr);
    return sortDir === 'asc' ? cmp : -cmp;
  });

  return (
    <div>
      {/* ── Sticky top bar ────────────────────────────────────────────── */}
      <div className="sticky top-0 z-10 -mx-4 md:-mx-6 lg:-mx-8 mb-6 bg-background border-b">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 md:px-6 lg:px-8 py-4">
          <h1 className="text-2xl font-bold tracking-tight">{t('ingredients.title')}</h1>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder={t('common.search')}
                className="pl-9 w-48 sm:w-64"
              />
            </div>
            {isAdmin && (
              <Button asChild size="sm">
                <Link to="/ingredients/new"><Plus size={16} />{t('ingredients.new')}</Link>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Desktop table */}
      <Card className="hidden md:block">
        {isLoading ? (
          <Spinner className="py-16" />
        ) : data?.data.length === 0 ? (
          <EmptyState message={t('common.noResults')}>
            {isAdmin && (
              <Button asChild size="sm" variant="outline">
                <Link to="/ingredients/new"><Plus size={14} /> {t('ingredients.new')}</Link>
              </Button>
            )}
          </EmptyState>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <SortHead col="name">{t('ingredients.name')}</SortHead>
                <SortHead col="supplier_name">{t('ingredients.supplier')}</SortHead>
                <SortHead col="category_name">{t('ingredients.category')}</SortHead>                
                <SortHead col="gross_cost_per_kg" right>{t('ingredients.grossCostPerKg')}</SortHead>
                <SortHead col="sugars" right>{t('ingredients.sugars')}</SortHead>
                <SortHead col="fat" right>{t('ingredients.fat')}</SortHead>
                <SortHead col="pac" right>{t('ingredients.pac')}</SortHead>
                <TableHead>{t('common.status')}</TableHead>
                {isAdmin && <TableHead />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedData.map(ing => (
                <TableRow key={ing.id} className="cursor-pointer" onClick={() => navigate(`/ingredients/${ing.id}`)}>
                  <TableCell className="font-medium">{ing.name}</TableCell>
                  <TableCell>{ing.supplier_name ?? '—'}</TableCell>
                  <TableCell>{ing.category_name ?? '—'}</TableCell>                  
                  <TableCell className="text-right">{formatCost(ing.gross_cost_per_kg)}</TableCell>
                  <TableCell className="text-right">{f2(ing.sugars)}%</TableCell>
                  <TableCell className="text-right">{f2(ing.fat)}%</TableCell>
                  <TableCell className="text-right">{f2(ing.pac)}</TableCell>
                  <TableCell>
                    <Badge variant={ing.is_active ? 'success' : 'secondary'}>
                      {ing.is_active ? t('common.active') : t('common.inactive')}
                    </Badge>
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="w-20" onClick={e => e.stopPropagation()}>
                      <div className="flex gap-1">
                        <Button asChild variant="ghost" size="icon">
                          <Link to={`/ingredients/${ing.id}/edit`}><Pencil size={14} /></Link>
                        </Button>
                        <Button
                          variant="ghost" size="icon"
                          className="text-destructive"
                          onClick={() => setDeleteId(ing.id)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {isLoading ? (
          <Spinner className="py-12" />
        ) : (data?.data ?? []).map(ing => (
          <Card key={ing.id} className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-semibold">{ing.name}</p>
                <p className="text-xs text-muted-foreground">{ing.category_name} · {ing.supplier_name}</p>
              </div>
              <Badge variant={ing.is_active ? 'success' : 'secondary'}>
                {ing.is_active ? t('common.active') : t('common.inactive')}
              </Badge>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
              <div><span className="text-muted-foreground">Cost/kg </span>{formatCost(ing.gross_cost_per_kg)}</div>
              <div><span className="text-muted-foreground">Sugars </span>{f2(ing.sugars)}%</div>
              <div><span className="text-muted-foreground">PAC </span>{f2(ing.pac)}</div>
            </div>
            {isAdmin && (
              <div className="mt-3 flex gap-2">
                <Button asChild variant="outline" size="sm" className="flex-1">
                  <Link to={`/ingredients/${ing.id}/edit`}><Pencil size={13} /> {t('common.edit')}</Link>
                </Button>
                <Button variant="outline" size="sm" className="text-destructive" onClick={() => setDeleteId(ing.id)}>
                  <Trash2 size={13} />
                </Button>
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Pagination */}
      {data && data.meta.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
          <span>Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, data.meta.total)} of {data.meta.total}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= data.meta.totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteId}
        message={t('ingredients.deleteConfirm')}
        onConfirm={() => deleteId && deleteMutation.mutate(deleteId)}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}
